// UI for user login via Google or anonymous fallback.
import { useEffect, useState } from 'react';
import { useT } from '@/i18n/useT';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ACCOUNT_DELETED_LOGIN_QUERY } from '@/features/settings/hooks/useAccountDelete';
import { useAuth } from '@/features/auth/useAuth';
import LoginEmailAuthPanel from '@/features/auth/LoginEmailAuthPanel';
import {
  signInWithGoogle,
  signInAnonymouslyUser,
  resolveAuthErrorKey,
} from '@/services/authService';

const LoginPage = () => {
  const t = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [googleErrorKey, setGoogleErrorKey] = useState(null);
  const accountDeletedBanner =
    searchParams.get(ACCOUNT_DELETED_LOGIN_QUERY) === '1';

  const handleGoogleSignIn = async () => {
    setGoogleErrorKey(null);
    try {
      await signInWithGoogle();
    } catch (error) {
      const key = await resolveAuthErrorKey(error, error?.customData?.email);
      setGoogleErrorKey(key);
    }
  };

  useEffect(() => {
    // If user is logged in, redirect to the main app start page
    if (user) {
      navigate('/app/today', { replace: true });
    }
  }, [user, navigate]);

  const isLocal =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  return (
    <div className="login-container" data-testid="login-page-root">
      <h1>{t('login.title', 'Welcome to Riposo')}</h1>
      {accountDeletedBanner ? (
        <p role="status" data-testid="login-account-deleted-banner">
          {t('login.accountDeleted')}
        </p>
      ) : null}
      <LoginEmailAuthPanel
        onGoogleSignIn={handleGoogleSignIn}
        googleErrorKey={googleErrorKey}
        onPanelInteraction={() => setGoogleErrorKey(null)}
      />
      {isLocal || import.meta.env.VITE_USE_EMULATORS === 'true' ? (
        <button
          id="anonymous-sign-in-button"
          style={{
            position: 'fixed',
            bottom: '10px',
            left: '10px',
            zIndex: 9999,
            padding: '10px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
          onClick={() => {
            signInAnonymouslyUser().catch(() => {});
          }}
        >
          {t('login.testUser', 'Test User Sign In (Test Only)')}
        </button>
      ) : null}
    </div>
  );
};

export default LoginPage;
